// 本人能力（人資 / 系統管理員）— sidebar/動作可見度 gate 用。真實存取由後端 RLS
// 把關，這只是避免顯示點了會 403 的入口。
import { useQuery } from '@tanstack/react-query';
import { useActor } from './actor-store.ts';
import { type ApiError, api } from './api.ts';

export interface MyCapabilities {
  is_hr: boolean;
  is_sysadmin: boolean;
  /** V318: 財務主管 — 財務模組(帳款/會計/報表/收入/GP) 可見度。 */
  is_finance: boolean;
  /** V318: 出納 — 記付款。 財務模組同樣可見。 */
  is_cashier: boolean;
  /** V319: 投資主管 — 投資模組(投資者名單)可見度。 */
  is_investment: boolean;
  /** V420: 開票 — 服務發票 / 管理費發票的執行關。 不進任何模組群組。 */
  is_invoicing: boolean;
  /** V467: 看得到財務報表 (唯讀) —— 管理員 / 財務主管 / 合夥人以上 */
  can_view_books: boolean;
}

export function useMyCapabilities() {
  const actor = useActor((s) => s.actor);
  return useQuery<MyCapabilities, ApiError>({
    queryKey: ['me-capabilities', actor],
    queryFn: () => api<MyCapabilities>('/api/v1/me/capabilities', { actor }),
    staleTime: 5 * 60_000,
    // 非內部 actor（lp）不打；沒登入時也不需要
    enabled: typeof actor !== 'object',
  });
}

/** True 當本人有人資能力（用於 HRM sidebar 群組可見度）。 */
export function useIsHr(): boolean {
  return useMyCapabilities().data?.is_hr ?? false;
}

/**
 * True 當本人有**財務主管**能力。
 *
 * ⚠ 2026-08-25 收窄：原本是 `is_finance || is_cashier`，而側邊欄的 `financeOnly`
 * 項目判斷式就是 `item.financeOnly && isFinance` —— 於是**出納其實五項全看得到**
 * （財務報表 / 收入 / 帳款 / 會計 / 記帳主體），V320 加的 `cashierOnly` 旗標形同
 * 虛設。 兩層講不同的話：後端已經把出納擋在報表外，前端卻照樣把入口給他。
 * 使用者定案「出納只負責付錢」→ 這個 hook 從此只回財務主管。
 * 要問「財務模組看不看得到」用 `useCanSeeFinanceNav()`。
 */
export function useIsFinance(): boolean {
  return useMyCapabilities().data?.is_finance ?? false;
}

/** 財務模組群組的可見度：財務主管**或**出納（出納只會看到掛 cashierOnly 的那幾項）。 */
export function useCanSeeFinanceNav(): boolean {
  const d = useMyCapabilities().data;
  return (d?.is_finance ?? false) || (d?.is_cashier ?? false);
}

/** True 當本人有投資主管能力（用於投資模組 sidebar 群組可見度）。 */
export function useIsInvestment(): boolean {
  return useMyCapabilities().data?.is_investment ?? false;
}

/** True 當本人有出納能力（帳款 + 銀行對帳；不含分錄/財報/收入）。 */
export function useIsCashier(): boolean {
  return useMyCapabilities().data?.is_cashier ?? false;
}

/** True 當本人有資訊主管（=系統管理員）能力。 */
export function useIsSysadminCap(): boolean {
  return useMyCapabilities().data?.is_sysadmin ?? false;
}

/** True 當本人有開票能力（V420）。 目前沒有側邊欄項目靠它 —— 開票的工作在簽核待辦裡。 */
export function useIsInvoicing(): boolean {
  return useMyCapabilities().data?.is_invoicing ?? false;
}

/**
 * V467 看帳（唯讀財務報表）—— 管理員 / 財務主管 / **合夥人以上**。
 *
 * 2026-09-20 使用者:「他是合夥人 同時如果我給他 叁石跟碩美泰的權限 他可以自動看到這兩間
 * 公司的帳」。 看得到**哪幾家**由記帳主體指派決定（後端 RLS），這個 hook 只答「能不能看帳」。
 * ⚠ 能看不等於能審 —— 簽核要有對應能力 + 指派（見 LedgerAssignmentsTab）。
 */
export function useCanViewBooks(): boolean {
  return useMyCapabilities().data?.can_view_books ?? false;
}
